import { Button, FormControl, Input, Select } from '@farol-ds/react'
import { FormEvent, useState } from 'react'

import type { SignupFormData } from '../../api/types'
import { BASE_PATH_B2B, OCCUPATION_IDS } from '../../constants'
import { ERROR_MESSAGES } from '../../utils/errorMessages'
import { parseErrorMessage } from '../../utils/parseErrorMessage'
import { MIN_SIZE, validatePasswordRules } from '../../utils/passwordStrength'
import { LoginCard } from '../login-card'
import { PasswordField } from '../password-field/password-field'
import styles from './complete-data-form.module.scss'

interface CompleteDataFormProps {
  email: string
  isLoading?: boolean
  error?: string
  basePath?: string
  onSubmit: (data: SignupFormData) => Promise<void>
  lockedOccupation?: string | null
  centerHeader?: boolean
}

type FieldName = 'fullName' | 'password' | 'occupation'

export function CompleteDataForm({
  email,
  isLoading = false,
  error,
  basePath = '',
  onSubmit,
  lockedOccupation,
  centerHeader = false,
}: CompleteDataFormProps) {
  const isB2B = basePath === BASE_PATH_B2B
  const hideOccupation = !isB2B && Boolean(lockedOccupation)
  const [fullNameValue, setFullNameValue] = useState('')
  const [passwordValue, setPasswordValue] = useState('')
  const [occupationValue, setOccupationValue] = useState(lockedOccupation ?? '')
  const [fieldErrors, setFieldErrors] = useState<{
    fullName?: string
    password?: string
    occupation?: string
  }>({})
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  const { lawyer, undergraduate, bachelor, other } = OCCUPATION_IDS

  const occupationOptions = [
    { value: lawyer, label: 'Advogado(a)' },
    { value: undergraduate, label: 'Estudante de Direito' },
    { value: bachelor, label: 'Bacharel em Direito' },
    { value: other, label: 'Outra profissão' },
  ]

  const validateFullName = (name: string): string | undefined => {
    const trimmed = name.trim()
    if (!trimmed) {
      return ERROR_MESSAGES.signup.details.name.empty
    }
    const nameParts = trimmed.split(/\s+/).filter((part) => part.length > 0)
    if (nameParts.length < 2) {
      return ERROR_MESSAGES.signup.details.name.invalid
    }
    return undefined
  }

  const validatePassword = (password: string): string | undefined => {
    const validation = validatePasswordRules(password, MIN_SIZE, fullNameValue, email)
    return validation.errorMessage
  }

  const validateOccupation = (occupation: string): string | undefined => {
    if (!occupation) {
      return ERROR_MESSAGES.signup.details.occupation.empty
    }
    return undefined
  }

  const fieldValidators = {
    fullName: validateFullName,
    password: validatePassword,
    occupation: validateOccupation,
  } as const

  const fieldValues = {
    fullName: fullNameValue,
    password: passwordValue,
    occupation: occupationValue,
  } as const

  const updateFieldError = (fieldName: FieldName, error: string | undefined) => {
    setFieldErrors((prev) => {
      if (error) {
        return { ...prev, [fieldName]: error }
      }
      const { [fieldName]: _, ...rest } = prev
      return rest
    })
  }

  const markFieldAsTouched = (fieldName: FieldName) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName))
  }

  const validateField = (fieldName: FieldName, value: string) => {
    const validator = fieldValidators[fieldName]
    return validator(value)
  }

  const handleFieldChange = (
    fieldName: FieldName,
    value: string,
    setValue: (value: string) => void,
    validateOnChange = false
  ) => {
    setValue(value)
    const shouldValidate = touchedFields.has(fieldName) || (validateOnChange && value.length > 0)
    if (shouldValidate) {
      if (!touchedFields.has(fieldName)) {
        markFieldAsTouched(fieldName)
      }
      const error = validateField(fieldName, value)
      updateFieldError(fieldName, error)
    }
  }

  const handleFieldBlur = (fieldName: FieldName) => {
    markFieldAsTouched(fieldName)
    const value = fieldValues[fieldName]
    const error = validateField(fieldName, value)
    updateFieldError(fieldName, error)
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const errors: typeof fieldErrors = {}
    const fieldNames: FieldName[] = isB2B ? ['fullName'] : ['fullName', 'password', 'occupation']

    fieldNames.forEach((fieldName) => {
      const value = fieldValues[fieldName]
      const error = validateField(fieldName, value)
      if (error) {
        errors[fieldName] = error
      }
    })

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setTouchedFields(new Set(fieldNames))
      return
    }

    setFieldErrors({})
    await onSubmit({
      email,
      fullName: fullNameValue,
      password: isB2B ? '' : passwordValue,
      occupation: isB2B ? '' : occupationValue,
    })
  }

  const getFieldError = (fieldName: FieldName) => {
    return touchedFields.has(fieldName) ? fieldErrors[fieldName] : undefined
  }

  const hasFieldError = (fieldName: FieldName) => {
    return touchedFields.has(fieldName) && !!fieldErrors[fieldName]
  }

  const parsedError = error ? parseErrorMessage(error) : null

  const header = {
    ...(centerHeader && { align: 'center' as const }),
    title: 'Complete seus dados',
    subtitle: {
      content: 'Insira seus dados para prosseguir com a criação de sua conta Jusbrasil.',
    },
    chip: {
      text: email,
    },
    ...(parsedError && {
      errorMessage: {
        title: parsedError.title,
        description: parsedError.description,
      },
    }),
  }

  return (
    <LoginCard header={header} hideCloseButton>
      <form onSubmit={handleSubmit} className={styles.form} noValidate>
        <FormControl error={hasFieldError('fullName')}>
          <FormControl.Label>Nome completo</FormControl.Label>
          <Input
            type="text"
            name="fullName"
            value={fullNameValue}
            onChange={(e) => handleFieldChange('fullName', e.target.value, setFullNameValue)}
            onBlur={() => handleFieldBlur('fullName')}
            autoComplete="name"
            disabled={isLoading}
          />
          {hasFieldError('fullName') && (
            <FormControl.ErrorMessage>{fieldErrors.fullName}</FormControl.ErrorMessage>
          )}
        </FormControl>

        {!isB2B && (
          <>
            <PasswordField
              value={passwordValue}
              onChange={(value) => handleFieldChange('password', value, setPasswordValue, true)}
              onBlur={() => handleFieldBlur('password')}
              disabled={isLoading}
              fullName={fullNameValue}
              email={email}
              error={getFieldError('password')}
            />

            {!hideOccupation && (
              <FormControl error={hasFieldError('occupation')}>
                <FormControl.Label>Ocupação</FormControl.Label>
                <Select
                  value={occupationValue}
                  onValueChange={(value) =>
                    handleFieldChange('occupation', value, setOccupationValue)
                  }
                  onOpenChange={(open) => {
                    if (!open) {
                      handleFieldBlur('occupation')
                    }
                  }}
                  disabled={isLoading}
                  placeholder=""
                >
                  {occupationOptions.map((option) => (
                    <Select.Item key={option.value} value={option.value}>
                      {option.label}
                    </Select.Item>
                  ))}
                </Select>
                {hasFieldError('occupation') && (
                  <FormControl.ErrorMessage>{fieldErrors.occupation}</FormControl.ErrorMessage>
                )}
              </FormControl>
            )}
          </>
        )}

        <Button type="submit" kind="primary" loading={isLoading}>
          Continuar
        </Button>
      </form>
    </LoginCard>
  )
}
